import type { QuestionWithSlide } from '@mentimeter/voting-schema/api-types-overrides';
import * as React from 'react';
import { MentiError } from '@mentimeter/error-utils/error';
import { useAppDispatch, useAppSelector } from '../../redux-hooks';
import { voteKeySelector } from '../../selectors';
import { useTrackVote } from '../../utils/tracker';
import { NO_VOTE } from '../../constants/errorHandling';
import { submitVote } from '../../actions/series';
import { skipQuestion } from '../../reducers/votingSlice';
import type { SubmitVoteBody } from '../../types';

export const useActions = (question: QuestionWithSlide) => {
  const failure = useAppSelector((state) => state.voting.failure);
  const isLoading = useAppSelector(
    (state) => state.voting.loading === 'pending',
  );
  const voteKey = useAppSelector(voteKeySelector);
  const dispatch = useAppDispatch();

  const trackVote = useTrackVote();
  const [error, setError] = React.useState<string>();

  const vote = async (voteBody: SubmitVoteBody) => {
    setError(undefined);
    if (!question) {
      throw new Error('No question defined');
    }

    const hasNoSubmission = !voteBody.isMigrated && !voteBody.payload.vote; // migrated series are more strictly typed

    if (hasNoSubmission) {
      setError(NO_VOTE);
      return Promise.resolve({
        type: 'submitVote/rejected',
        payload: NO_VOTE,
      });
    }

    const isTypeMismatched =
      !question.isMigrated && voteBody.payload.type !== question.type;
    if (isTypeMismatched) {
      return Promise.reject(
        new MentiError(
          `The submission type (${voteBody.payload.type}) was different from the question type (${question.type})`,
          {
            feature: 'submit-response',
          },
        ),
      );
    }

    trackVote();
    return dispatch(submitVote({ ...voteBody, voteKey })).then((res) => ({
      type: res.type,
      payload: res.payload,
    }));
  };

  return {
    vote,
    skip: () => {
      if (!question) {
        throw new Error('No question defined');
      }
      dispatch(skipQuestion());
    },
    error: error || failure,
    isLoading,
  };
};
